/**
 * Who is signed in (unit 26A, item 4). `GET /api/session` answers the person,
 * or 401 for no one.
 *
 * **With no signal the app still opens**, as the person it was last open for:
 * the offline cache is theirs, bound to their account (25B rule 7), so their
 * rows show and their writes queue. The last answer is kept per browser for
 * exactly that, and used only when the server cannot be asked at all.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type Schemas } from "../api/client";
import { ApiError, ok } from "../api/query";

export type Me = Schemas["Me"];

export const SESSION = ["session"] as const;
const LAST = "kindred:last-session";

function remember(me: Me | null): void {
  try {
    if (me) localStorage.setItem(LAST, JSON.stringify(me));
    else localStorage.removeItem(LAST);
  } catch {
    // Best effort: without it, an open with no signal shows the sign-in screen.
  }
}

function lastKnown(): Me | null {
  try {
    const stored = localStorage.getItem(LAST);
    return stored ? (JSON.parse(stored) as Me) : null;
  } catch {
    return null;
  }
}

export async function fetchSession(): Promise<Me | null> {
  let answer;
  try {
    answer = await api.GET("/api/session");
  } catch {
    return lastKnown();
  }
  if (answer.response.status === 401) {
    remember(null);
    return null;
  }
  const me = await ok(Promise.resolve(answer));
  remember(me);
  return me;
}

export function useSession() {
  return useQuery({ queryKey: SESSION, queryFn: fetchSession, staleTime: Infinity, retry: false });
}

export function useSignIn() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas["SignIn"]) => ok(api.POST("/api/session", { body })),
    onSuccess: (me) => {
      remember(me);
      client.setQueryData(SESSION, me);
    },
  });
}

export function useSignOut() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => ok(api.DELETE("/api/session")),
    onSettled: () => {
      remember(null);
      // The gate first, so nothing still mounted reads on; then the person's answers go.
      client.setQueryData(SESSION, null);
      client.removeQueries({ predicate: (query) => query.queryKey[0] !== SESSION[0] });
    },
  });
}

/** A 401 on any read means the session ended: back to the sign-in screen. */
export function isSignedOut(error: unknown): boolean {
  return error instanceof ApiError && error.status === 401;
}
